import React from 'react';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import LanguageIcon from '@mui/icons-material/Language';

// Mapping from language code to primary country code for flags
const langToCountry: Record<string, string> = {
  nl: 'NL',
  es: 'ES',
  ko: 'KR',
  it: 'IT',
  de: 'DE',
  th: 'TH',
  ru: 'UN', // use UN flag as universal fallback
  pt: 'PT',
  pl: 'PL',
  id: 'ID',
  zh: 'CN',
  sv: 'SE',
  cs: 'CZ',
  en: 'GB',
  ja: 'JP',
  fr: 'FR',
  ro: 'RO',
  tr: 'TR',
  ca: 'ES',
  hu: 'HU',
  uk: 'UA',
  el: 'GR',
  bg: 'BG',
  ar: 'SA',
  sr: 'RS',
  mk: 'MK',
  lv: 'LV',
  sl: 'SI',
  hi: 'IN',
  gl: 'ES',
  da: 'DK',
  ur: 'PK',
  sk: 'SK',
  he: 'IL',
  fi: 'FI',
  az: 'AZ',
  lt: 'LT',
  et: 'EE',
  no: 'NO',
  cy: 'GB',
  fa: 'UN', // use UN flag as universal fallback
  vi: 'VN',
  ta: 'IN',
  ms: 'MY',
  hr: 'HR',
  tl: 'PH',
  bs: 'BA',
  is: 'IS',
};

export interface LanguageAvatarProps extends Omit<AvatarProps, 'children'> {
  /** Two-letter ISO language code */
  language: string;
}

/**
 * Renders a language avatar: flag for primary country or uppercase language code.
 */
const LanguageAvatar: React.FC<LanguageAvatarProps> = ({ language, sx, ...props }) => {
  const code = (language || '').toLowerCase();
  const country = langToCountry[code];
  // Avatar size settings
  const defaultSx = { width: 24, height: 24 };
  if (country) {
    // Flag icon as background image, cropped to circle via object-fit
    const flagUrl = `https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/${country.toLowerCase()}.svg`;
    return (
      <Avatar
        src={flagUrl}
        alt={country}
        imgProps={{ style: { objectFit: 'cover' } }}
        sx={{
          ...defaultSx,
          ...(sx as Record<string, any>),
        }}
        {...props}
      />
    );
  }
  // Fallback: show generic language icon
  return (
    <Avatar
      sx={{
        ...defaultSx,
        bgcolor: 'primary.main',
        color: 'common.white',
        fontSize: 'inherit',
        ...(sx as Record<string, any>),
      }}
      {...props}
    >
      <LanguageIcon fontSize="inherit" />
    </Avatar>
  );
};

export default LanguageAvatar;